import type { AdSettings, AdSlotKey } from "@gooner/shared";

/**
 * What an ad slot shows — the decisions AdSlot makes, pulled out so a check can run them.
 *
 * THE HOUSE CARD (2026-10-21, tip 5 option C). A slot whose zone never produced an
 * iframe — the script was blocked or failed to load, or nothing arrived by the deadline —
 * used to collapse. On Browse that left the grid's reserved 270px cell as an empty band,
 * so it now shows our own card instead: the Premium pitch ("no ads" is its first perk),
 * or the guide when Premium is not on sale.
 *
 * WHAT IT CANNOT CATCH: a slot that got an iframe is "filled" and stays as it is, even
 * when the network's creative is its own "AdServer 404" house page. That iframe is
 * cross-origin and posts nothing, so the parent cannot tell it from a real ad.
 */

/** The gates in front of any network request, unchanged: ads off or no zone shows
 *  nothing, safe mode shows the inert grey box, and only an explicit "0" goes live. */
export const BORROWS_BROWSE_ZONE: ReadonlySet<AdSlotKey> = new Set<AdSlotKey>([
  "collectionsInFeed",
  "feedInFeed",
  "roomsList",
  "collectionPage",
]);

export function adSlotGate(
  ads: AdSettings | undefined,
  slot: AdSlotKey,
  safeMode: string | undefined,
): { kind: "off" } | { kind: "placeholder" } | { kind: "live"; zone: string } {
  // The newer 300x250 units borrow the Browse in-feed zone until they are given their own,
  // so they earn from day one. Same size, and each on its own page, never with Browse's,
  // so the shared id is never on one page twice.
  const own = ads?.zones?.[slot]?.trim();
  const borrowed = BORROWS_BROWSE_ZONE.has(slot) ? ads?.zones?.browseInFeed?.trim() : undefined;
  const zone = own || borrowed;
  if (!ads?.enabled || !zone) return { kind: "off" };
  return safeMode === "0" ? { kind: "live", zone } : { kind: "placeholder" };
}

/** A live slot once something has happened: an iframe means filled, full stop; a
 *  blocked script or a passed deadline without one means the house card. */
export type AdOutcome = "filled" | "house" | "waiting";
export function adOutcome(e: { iframe: boolean; scriptFailed: boolean; deadlinePassed: boolean }): AdOutcome {
  if (e.iframe) return "filled";
  if (e.scriptFailed || e.deadlinePassed) return "house";
  return "waiting";
}

/**
 * How the card lays out, from the slot's configured unit width.
 *
 * A unit narrower than NARROW_SLOT_MAX_WIDTH (Browse's 160px skyscraper in the 224px
 * sidebar) gets the COMPACT card: everything stacked and centred — "No ads" on one line,
 * one short sentence, the button underneath. The full sentence and a side-by-side button
 * need ~300px; at the skyscraper's ~134px of content the row layout wrapped the title,
 * ran the body 1–2 words a line and pushed the button over the text (QA 2026-10-21).
 * Decided from the configured width, not measured, so the first paint is already right.
 */
export const NARROW_SLOT_MAX_WIDTH = 240;
export function houseCardLayout(unitWidth: number): "compact" | "row" {
  return unitWidth < NARROW_SLOT_MAX_WIDTH ? "compact" : "row";
}

/** The compact card's one short line under "No ads" — the long sub does not fit a skyscraper. */
export const COMPACT_NO_ADS_SUB = "Never with Premium.";

/**
 * The share of live ad slots handed to the shop outright, before any ad is asked for. The
 * owner's call (2026-11-03): the ads earn little, so half the slots promote a Featured
 * product instead. `draw` is a 0-1 number, taken once per mount.
 */
export const SHOP_TURN_SHARE = 0.5;
export function takesShopTurn(draw: number): boolean {
  return draw < SHOP_TURN_SHARE;
}

/**
 * Which house card. Without a shop product to show: the Premium pitch while Premium is on
 * sale, the guide otherwise. With one, the shop takes the guide's place outright, and shares
 * the Premium pitch's impressions half and half (`coin` is a 0-1 draw, once per mount) so
 * promoting the shop doesn't cost Premium every one of its fallback views.
 */
export function houseCardKind(sellingEnabled: boolean, hasShopPromo = false, coin = 1): "premium" | "how" | "shop" {
  if (hasShopPromo && (!sellingEnabled || coin < 0.5)) return "shop";
  return sellingEnabled ? "premium" : "how";
}
